import { BackgroundAudio } from "../audio/BackgroundAudio";
import TimeoutError from "../dth/clientHttp/errors/TimeoutError";

/**
 * Identificatori logici dei suoni gestiti dal SoundManager.
 *
 * - `outgoingCall` → suoneria chiamata in arrivo (loop)
 * - `ingoingCall`  → suono di chiamata in corso (loop)
 * - `busy`         → tono di linea occupata
 * - `callend`      → suono di fine chiamata
 * - `error`        → segnale di errore
 * - `waiting`      → ringback / attesa risposta
 */
export type SoundId = "outgoingCall" | "ingoingCall" | "busy" | "callend" | "error" | "waiting";

/**
 * Configurazione di un singolo suono.
 */
interface SoundConfig {
    /**
     * Percorso base del file audio (senza estensione).
     * Il formato effettivo viene scelto automaticamente.
     *
     * Esempio: "media/ring"
     */
    path: string;

    /**
     * Indica se il suono deve essere riprodotto in loop.
     */
    loop: boolean;
}

/**
 * Gestore centralizzato dei suoni applicativi.
 *
 * Responsabilità:
 * - riprodurre suoni one-shot e suoni in loop
 * - garantire che lo `stop()` fermi SEMPRE il suono
 * - evitare sovrapposizioni indesiderate dello stesso suono
 * - gestire correttamente race condition (play → stop → load)
 *
 * Ogni suono è identificato tramite un `SoundId`
 * ed è attivo in un solo esemplare alla volta.
 *
 * Questa classe è pensata per contesti VoIP / softphone,
 * ma è riutilizzabile in qualsiasi app web.
 */
export class SoundManager {

    /**
     * Istanza di BackgroundAudio condivisa.
     */
    private audio = new BackgroundAudio();

    /**
     * Mappa dei suoni attualmente in riproduzione.
     *
     * Chiave: SoundId
     * Valore: AudioBufferSourceNode attivo
     */
    private sources = new Map<SoundId, AudioBufferSourceNode>();

    /**
     * La variabile playTokens è una mappa che tiene traccia di un "token" numerico 
     * per ogni suono gestito. Il suo ruolo è garantire che ogni chiamata a play() o stop() 
     * per un suono sia identificata da un token univoco. 
     * Questo serve a evitare che suoni obsoleti vengano riprodotti se, ad esempio, 
     * arriva uno stop o un altro play mentre il suono è ancora in fase di caricamento.
     * 
     * In pratica:
     *  - Quando chiami play(id), il token per quel suono viene incrementato e salvato.
     *  - Se durante il caricamento del suono arriva un altro play() o uno stop(), il token cambia.
     *  - Quando il suono è pronto per essere riprodotto, si controlla che il token non sia cambiato: se è cambiato, il suono viene subito fermato perché la richiesta è ormai obsoleta.
     *  - Questo meccanismo previene la riproduzione di suoni non desiderati in caso di chiamate rapide o concorrenti a play/stop.
     */
    private playTokens = new Map<SoundId, number>();

    /**
     * Coda seriale per id: garantisce che play/stop sullo stesso id
     * non si sovrappongano mai, anche con await in mezzo
     */
    private queues = new Map<SoundId, Promise<void>>();

    /**
     * Configurazione statica dei suoni gestiti.
     */
    private sounds = new Map<SoundId, SoundConfig>([
        ["ingoingCall", { path: "media/ring", loop: true }],
        ["outgoingCall", { path: "media/calling", loop: true }],
        ["busy", { path: "media/busy", loop: false }],
        ["callend", { path: "media/callend", loop: false }],
        ["error", { path: "media/error", loop: false }],
        ["waiting", { path: "media/ringback", loop: false }],
    ]);

    /**
     * Serializza le operazioni sullo stesso id tramite una Promise chain.
     * Ogni operazione aspetta il completamento della precedente.
     */
    private enqueue(id: SoundId, fn: () => Promise<void>): Promise<void> {
        // Prende l'ultima Promise in coda per questo id, o una resolved se non c'è
        const prev = this.queues.get(id) ?? Promise.resolve();
        // ogni task nella coda ignora errori del precedente per non bloccare la coda
        const next = prev.then(() => fn()).catch(() => { });
        // Metti next in coda per questo id
        this.queues.set(id, next);
        return next;
    }


    /**
     * Avvia la riproduzione di un suono.
     * Se il suono è già attivo, lo ferma e lo riavvia.
     * Le chiamate parallele sullo stesso id sono serializzate.
     */
    play(id: SoundId): Promise<void> {
        console.debug(`[SoundManager]: play('${id}') requested`);
        return this.enqueue(id, () => this._play(id));
    }

    private async _play(id: SoundId): Promise<void> {
        console.debug(`[SoundManager]: _play('${id}')`);

        // Ferma source attivo se presente
        this._stopSource(id);

        const token = (this.playTokens.get(id) ?? 0) + 1;
        this.playTokens.set(id, token);

        const cfg = this.sounds.get(id);
        if (!cfg) {
            console.error(`[SoundManager]: config not found for '${id}'`);
            return;
        }

        let src: AudioBufferSourceNode | undefined = undefined;
        const abortController = new AbortController();
        // timeout di 3 secondi per il caricamento del suono: 
        // se scade, abortisce fetch e decode, e ignora il risultato se arriva dopo
        let timedOut = false;
        const playPromise = this.audio.pickFormatAndPlay(cfg.path, ["mp3", "ogg"], cfg.loop, abortController)
            .then(result => {
                src = result;
                // Se siamo qui dopo il timeout, 
                // fermiamo subito la source e non riproduciamo
                if (timedOut && src) {
                    try { src.stop(0); } catch (stopErr) { console.warn(`[SoundManager]: failed to stop '${id}' after timeout`, stopErr); }
                    throw new Error(`Audio load completed after timeout for '${id}'`);
                }
                return result;
            });
        try {
            src = await Promise.race([
                playPromise,
                new Promise<never>((_, reject) =>
                    setTimeout(() => {
                        timedOut = true;
                        abortController.abort();
                        reject(new TimeoutError(`Audio load timeout for '${id}'`));
                    }, 3000)
                ),
            ]);
        } catch (err) {
            console.error(`[SoundManager]: failed to load '${id}'`, err);
            return;
        }

        // Token cambiato durante l'await → questa richiesta è obsoleta
        if (this.playTokens.get(id) !== token) {
            console.debug(`[SoundManager]: '${id}' token stale, discarding`);
            try { src.stop(0); } catch (stopErr) { console.warn(`[SoundManager]: failed to stop '${id}' after token stale`, stopErr); }
            return;
        }

        this.sources.set(id, src);
        console.debug(`[SoundManager]: '${id}' active`);

        src.onended = () => {
            if (this.sources.get(id) === src) {
                this.sources.delete(id);
            }
        };
    }

    /**
     * Ferma un suono specifico.
     * Serializzato rispetto alle chiamate play() per lo stesso id.
     */
    stop(id: SoundId): Promise<void> {
        console.debug(`[SoundManager]: stop('${id}') requested`);
        return this.enqueue(id, async () => this._stopSource(id));
    }

    /**
     * Stop sincrono interno — usato sia da stop() che da _play().
     * Non entra in coda, va chiamato solo dall'interno della coda stessa.
     */
    private _stopSource(id: SoundId): void {
        console.debug(`[SoundManager]: _stopSource('${id}')`);
        // Invalida qualsiasi play() in attesa dell'await
        this.playTokens.set(id, (this.playTokens.get(id) ?? 0) + 1);

        const src = this.sources.get(id);
        this.sources.delete(id);

        if (src) {
            try { src.stop(0); } catch (stopErr) { console.warn(`[SoundManager]: failed to stop '${id}' in _stopSource`, stopErr); }
        }
    }

    /**
     * Ferma tutti i suoni, opzionalmente solo quelli in loop.
     */
    stopAll(onlyLoop = false): Promise<void[]> {
        const targets: SoundId[] = [];

        for (const [id, cfg] of this.sounds) {
            if (onlyLoop && !cfg.loop) continue;
            targets.push(id);
        }

        console.debug(`[SoundManager]: stopAll(onlyLoop=${onlyLoop}) →`, targets);
        return Promise.all(targets.map(id => this.stop(id)));
    }
}